import React, { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Admin UI for the `support` module (FAQ, App videos, NPS feedback).
 * Only rendered when the active sidebar item is "Support" and the user
 * has admin role (the backend enforces this too).
 */

const API = {
  faqs: "/api/support/faqs/",
  videos: "/api/support/videos/",
  nps: "/api/support/nps/",
  npsSummary: "/api/support/nps/summary/",
};

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : "";
}

async function apiRequest(url, options = {}) {
  const headers = {
    "X-Requested-With": "XMLHttpRequest",
    ...(options.headers || {}),
  };
  const method = (options.method || "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = readCookie("csrftoken");
    if (csrf) headers["X-CSRFToken"] = csrf;
  }
  const isFormData = options.body instanceof FormData;
  if (!isFormData && options.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers,
  });
  if (response.status === 204) return null;
  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await response.json() : await response.text();
  if (!response.ok) {
    const message = isJson
      ? (typeof payload === "string" ? payload : (payload?.detail || payload?.message || JSON.stringify(payload)))
      : `Server error (${response.status}) — this feature may not be available.`
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return payload;
}

const EMPTY_FAQ = {
  question: "",
  answer: "",
  category: "",
  sequence: 0,
  is_active: true,
};

const EMPTY_VIDEO = {
  title: "",
  description: "",
  sequence: 0,
  is_active: true,
  video_file: null,
  thumbnail: null,
};

function SupportAdminView() {
  const [activeTab, setActiveTab] = useState("faqs");

  return (
    <section className="toolbar-panel">
      <div className="toolbar-header">
        <h1>Support</h1>
        <div className="toolbar-actions">
          <button
            type="button"
            className={`secondary-ghost compact-button ${activeTab === "faqs" ? "active" : ""}`}
            onClick={() => setActiveTab("faqs")}
          >
            FAQs
          </button>
          <button
            type="button"
            className={`secondary-ghost compact-button ${activeTab === "videos" ? "active" : ""}`}
            onClick={() => setActiveTab("videos")}
          >
            Videos
          </button>
          <button
            type="button"
            className={`secondary-ghost compact-button ${activeTab === "nps" ? "active" : ""}`}
            onClick={() => setActiveTab("nps")}
          >
            NPS
          </button>
        </div>
      </div>

      {activeTab === "faqs" ? <FAQPanel /> : null}
      {activeTab === "videos" ? <VideoPanel /> : null}
      {activeTab === "nps" ? <NPSPanel /> : null}
    </section>
  );
}

/* ------------------------------- FAQ panel ------------------------------- */

function FAQPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FAQ);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest(`${API.faqs}?all=true&page_size=100`);
      setItems(Array.isArray(data?.results) ? data.results : data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function resetForm() {
    setForm(EMPTY_FAQ);
    setEditingId(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    try {
      const body = JSON.stringify({
        ...form,
        sequence: Number(form.sequence) || 0,
      });
      if (editingId) {
        await apiRequest(`${API.faqs}${editingId}/`, { method: "PATCH", body });
      } else {
        await apiRequest(API.faqs, { method: "POST", body });
      }
      resetForm();
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await apiRequest(`${API.faqs}${id}/`, { method: "DELETE" });
      if (editingId === id) resetForm();
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleToggleActive(item) {
    try {
      await apiRequest(`${API.faqs}${item.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !item.is_active }),
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  function handleEdit(item) {
    setForm({
      question: item.question || "",
      answer: item.answer || "",
      category: item.category || "",
      sequence: item.sequence || 0,
      is_active: Boolean(item.is_active),
    });
    setEditingId(item.id);
  }

  return (
    <div className="filter-panel filter-panel-inline" style={{ flexDirection: "column", alignItems: "stretch" }}>
      <form onSubmit={handleSubmit} className="filter-panel filter-panel-inline" style={{ border: "none", padding: 0 }}>
        <label className="field-stack" style={{ flex: "1 1 260px" }}>
          <span>Question</span>
          <input
            type="text"
            value={form.question}
            onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
            required
          />
        </label>
        <label className="field-stack" style={{ flex: "2 1 360px" }}>
          <span>Answer</span>
          <textarea
            rows={2}
            value={form.answer}
            onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
            required
          />
        </label>
        <label className="field-stack">
          <span>Category</span>
          <input
            type="text"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          />
        </label>
        <label className="field-stack" style={{ maxWidth: 110 }}>
          <span>Sequence</span>
          <input
            type="number"
            min={0}
            value={form.sequence}
            onChange={(e) => setForm((f) => ({ ...f, sequence: e.target.value }))}
          />
        </label>
        <label className="field-stack" style={{ maxWidth: 120, justifyContent: "flex-end" }}>
          <span>Active</span>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          />
        </label>
        <div className="filter-actions-inline" style={{ alignItems: "flex-end" }}>
          <button type="submit" className="primary-button add-button">
            {editingId ? "Update FAQ" : "Add FAQ"}
          </button>
          {editingId ? (
            <button type="button" className="secondary-ghost compact-button" onClick={resetForm}>
              Cancel
            </button>
          ) : null}
        </div>
      </form>

      {error ? <p style={{ color: "#c0392b" }}>{error}</p> : null}

      <SimpleTable
        loading={loading}
        empty="No FAQs yet."
        headers={["Seq", "Question", "Category", "Active", "Actions"]}
        rows={items.map((item) => [
          item.sequence,
          (
            <div key="q">
              <strong>{item.question}</strong>
              <div style={{ color: "#6f7f9d", marginTop: 4 }}>{item.answer}</div>
            </div>
          ),
          item.category || "—",
          (
            <button
              key="toggle"
              type="button"
              className="secondary-ghost compact-button"
              onClick={() => handleToggleActive(item)}
            >
              {item.is_active ? "Yes" : "No"}
            </button>
          ),
          (
            <div key="actions" style={{ display: "flex", gap: 6 }}>
              <button type="button" className="secondary-ghost compact-button" onClick={() => handleEdit(item)}>
                Edit
              </button>
              <button type="button" className="secondary-ghost compact-button" onClick={() => handleDelete(item.id)}>
                Delete
              </button>
            </div>
          ),
        ])}
      />
    </div>
  );
}

/* ------------------------------ Video panel ------------------------------ */

function VideoPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_VIDEO);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest(`${API.videos}?all=true&page_size=100`);
      setItems(Array.isArray(data?.results) ? data.results : data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function resetForm() {
    setForm(EMPTY_VIDEO);
    setEditingId(null);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    try {
      const data = new FormData();
      data.append("title", form.title);
      data.append("description", form.description || "");
      data.append("sequence", String(Number(form.sequence) || 0));
      data.append("is_active", form.is_active ? "true" : "false");
      if (form.video_file) data.append("video_file", form.video_file);
      if (form.thumbnail) data.append("thumbnail", form.thumbnail);
      if (editingId) {
        await apiRequest(`${API.videos}${editingId}/`, { method: "PATCH", body: data });
      } else {
        if (!form.video_file) throw new Error("Please choose a video file.");
        await apiRequest(API.videos, { method: "POST", body: data });
      }
      resetForm();
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this video?")) return;
    try {
      await apiRequest(`${API.videos}${id}/`, { method: "DELETE" });
      if (editingId === id) resetForm();
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleToggleActive(item) {
    try {
      await apiRequest(`${API.videos}${item.id}/toggle-active/`, { method: "POST" });
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  function handleEdit(item) {
    setForm({
      title: item.title || "",
      description: item.description || "",
      sequence: item.sequence || 0,
      is_active: Boolean(item.is_active),
      video_file: null,
      thumbnail: null,
    });
    setEditingId(item.id);
  }

  return (
    <div className="filter-panel filter-panel-inline" style={{ flexDirection: "column", alignItems: "stretch" }}>
      <form onSubmit={handleSubmit} className="filter-panel filter-panel-inline" style={{ border: "none", padding: 0 }}>
        <label className="field-stack" style={{ flex: "1 1 260px" }}>
          <span>Title</span>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            required
          />
        </label>
        <label className="field-stack" style={{ flex: "2 1 320px" }}>
          <span>Description</span>
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </label>
        <label className="field-stack" style={{ maxWidth: 110 }}>
          <span>Sequence</span>
          <input
            type="number"
            min={0}
            value={form.sequence}
            onChange={(e) => setForm((f) => ({ ...f, sequence: e.target.value }))}
          />
        </label>
        <label className="field-stack" style={{ maxWidth: 120, justifyContent: "flex-end" }}>
          <span>Active</span>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          />
        </label>
        <label className="field-stack">
          <span>Video file {editingId ? "(leave blank to keep)" : "*"}</span>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setForm((f) => ({ ...f, video_file: e.target.files?.[0] || null }))}
          />
        </label>
        <label className="field-stack">
          <span>Thumbnail</span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setForm((f) => ({ ...f, thumbnail: e.target.files?.[0] || null }))}
          />
        </label>
        <div className="filter-actions-inline" style={{ alignItems: "flex-end" }}>
          <button type="submit" className="primary-button add-button">
            {editingId ? "Update video" : "Upload video"}
          </button>
          {editingId ? (
            <button type="button" className="secondary-ghost compact-button" onClick={resetForm}>
              Cancel
            </button>
          ) : null}
        </div>
      </form>

      {error ? <p style={{ color: "#c0392b" }}>{error}</p> : null}

      <SimpleTable
        loading={loading}
        empty="No videos uploaded yet."
        headers={["Seq", "Title", "Video", "Active", "Actions"]}
        rows={items.map((item) => [
          item.sequence,
          (
            <div key="title">
              <strong>{item.title}</strong>
              {item.description ? (
                <div style={{ color: "#6f7f9d", marginTop: 4 }}>{item.description}</div>
              ) : null}
            </div>
          ),
          item.video_url ? (
            <a key="v" href={item.video_url} target="_blank" rel="noreferrer">
              Open
            </a>
          ) : (
            "—"
          ),
          (
            <button
              key="toggle"
              type="button"
              className="secondary-ghost compact-button"
              onClick={() => handleToggleActive(item)}
            >
              {item.is_active ? "Yes" : "No"}
            </button>
          ),
          (
            <div key="actions" style={{ display: "flex", gap: 6 }}>
              <button type="button" className="secondary-ghost compact-button" onClick={() => handleEdit(item)}>
                Edit
              </button>
              <button type="button" className="secondary-ghost compact-button" onClick={() => handleDelete(item.id)}>
                Delete
              </button>
            </div>
          ),
        ])}
      />
    </div>
  );
}

/* ------------------------------- NPS panel ------------------------------- */

function NPSPanel() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    vin: "",
    phone_number: "",
    parameter_name: "",
    min_rating: "",
    max_rating: "",
  });

  const queryString = useMemo(() => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) qs.set(k, String(v).trim());
    });
    qs.set("page_size", "50");
    return qs.toString();
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [listData, summaryData] = await Promise.all([
        apiRequest(`${API.nps}?${queryString}`),
        apiRequest(`${API.npsSummary}?${queryString}`),
      ]);
      setItems(Array.isArray(listData?.results) ? listData.results : listData || []);
      setSummary(summaryData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleDelete(id) {
    if (!window.confirm("Delete this NPS response?")) return;
    try {
      await apiRequest(`${API.nps}${id}/`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  function handleFilterChange(name, value) {
    setFilters((f) => ({ ...f, [name]: value }));
  }

  function handleReset() {
    setFilters({
      vin: "",
      phone_number: "",
      parameter_name: "",
      min_rating: "",
      max_rating: "",
    });
  }

  return (
    <div className="filter-panel filter-panel-inline" style={{ flexDirection: "column", alignItems: "stretch" }}>
      <div className="filter-panel filter-panel-inline" style={{ border: "none", padding: 0 }}>
        <label className="field-stack">
          <span>VIN</span>
          <input
            type="text"
            value={filters.vin}
            onChange={(e) => handleFilterChange("vin", e.target.value)}
          />
        </label>
        <label className="field-stack">
          <span>Phone</span>
          <input
            type="text"
            value={filters.phone_number}
            onChange={(e) => handleFilterChange("phone_number", e.target.value)}
          />
        </label>
        <label className="field-stack">
          <span>Parameter</span>
          <input
            type="text"
            value={filters.parameter_name}
            onChange={(e) => handleFilterChange("parameter_name", e.target.value)}
          />
        </label>
        <label className="field-stack" style={{ maxWidth: 110 }}>
          <span>Min rating</span>
          <input
            type="number"
            min={1}
            max={5}
            value={filters.min_rating}
            onChange={(e) => handleFilterChange("min_rating", e.target.value)}
          />
        </label>
        <label className="field-stack" style={{ maxWidth: 110 }}>
          <span>Max rating</span>
          <input
            type="number"
            min={1}
            max={5}
            value={filters.max_rating}
            onChange={(e) => handleFilterChange("max_rating", e.target.value)}
          />
        </label>
        <div className="filter-actions-inline" style={{ alignItems: "flex-end" }}>
          <button type="button" className="secondary-ghost compact-button" onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>

      {summary ? (
        <div
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            padding: "10px 0",
            color: "#2c3e50",
          }}
        >
          <span>
            <strong>Total:</strong> {summary.total}
          </span>
          <span>
            <strong>Average rating:</strong>{" "}
            {summary.average_rating != null ? Number(summary.average_rating).toFixed(2) : "—"}
          </span>
          {(summary.by_rating || []).map((row) => (
            <span key={row.rating_score}>
              <strong>{row.rating_score}★:</strong> {row.count}
            </span>
          ))}
        </div>
      ) : null}

      {error ? <p style={{ color: "#c0392b" }}>{error}</p> : null}

      <SimpleTable
        loading={loading}
        empty="No NPS responses match the current filters."
        headers={["When", "User", "Phone", "VIN", "Parameter", "Rating", "Comments", ""]}
        rows={items.map((item) => [
          new Date(item.created_at).toLocaleString(),
          item.user_name,
          item.phone_number,
          item.vin,
          item.parameter_name,
          `${item.rating_score}/5`,
          item.free_text ? <span title={item.free_text}>{item.free_text.slice(0, 80)}{item.free_text.length > 80 ? "…" : ""}</span> : "—",
          (
            <button
              key="del"
              type="button"
              className="secondary-ghost compact-button"
              onClick={() => handleDelete(item.id)}
            >
              Delete
            </button>
          ),
        ])}
      />
    </div>
  );
}

/* ------------------------------- Shared ui ------------------------------- */

function SimpleTable({ headers, rows, loading, empty }) {
  return (
    <div style={{ overflowX: "auto", marginTop: 12 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                style={{
                  textAlign: "left",
                  padding: "10px 8px",
                  borderBottom: "1px solid var(--line-strong, #d0d7de)",
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "#6f7f9d",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={headers.length} style={{ padding: 16, color: "#6f7f9d" }}>
                Loading…
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} style={{ padding: 16, color: "#6f7f9d" }}>
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row, r) => (
              <tr key={r} style={{ borderBottom: "1px solid rgba(208, 215, 222, 0.5)" }}>
                {row.map((cell, c) => (
                  <td
                    key={c}
                    style={{ padding: "10px 8px", verticalAlign: "top" }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default SupportAdminView;
